import { useCallback, useRef, useState } from "react";
import { useActiveContext } from "../contexts/ActiveContextContext";
import postsService from "../services/postsService";

export interface Post {
  id: number;
  content: string;
  platform?: string;
  status: string;
  agent_generated?: boolean;
  hashtags?: string[];
  call_to_action?: string;
  call_to_action_url?: string;
  image_info?: { filename: string };
  [key: string]: any;
}

export interface Stats {
  total_posts: number;
  pending_approval: number;
  approved: number;
  rejected: number;
  published: number;
  approval_rate: number;
  [key: string]: any;
}

export function usePosts() {
  const { activeContext } = useActiveContext();
  const [pendingPosts, setPendingPosts] = useState<Post[]>([]);
  const [approvedPosts, setApprovedPosts] = useState<Post[]>([]);
  const [publishedPosts, setPublishedPosts] = useState<Post[]>([]);
  const [rejectedPosts, setRejectedPosts] = useState<Post[]>([]);
  const [scheduledPosts, setScheduledPosts] = useState<Post[]>([]);
  const [allPosts, setAllPosts] = useState<Post[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingPosts, setDeletingPosts] = useState<Set<number>>(new Set());
  const [totalCounts, setTotalCounts] = useState<{ [key: string]: number }>({
    pending: 0,
    approved: 0,
    published: 0,
    rejected: 0,
    scheduled: 0,
    all: 0,
  });

  // Track context to reset state synchronously and avoid flicker
  const contextId = `${activeContext.type}-${activeContext.organization?.id || ""}-${activeContext.clientCompany?.id || ""}`;
  const [lastContextId, setLastContextId] = useState(contextId);
  const contextIdRef = useRef(contextId);
  const isLoadingRef = useRef(false);

  // Sync ref with current contextId
  contextIdRef.current = contextId;

  if (contextId !== lastContextId) {
    // Clear global cache when company changes to avoid stale data
    postsService.invalidateAllPosts();

    setPendingPosts([]);
    setApprovedPosts([]);
    setPublishedPosts([]);
    setRejectedPosts([]);
    setScheduledPosts([]);
    setAllPosts([]);
    setTotalCounts({
      pending: 0,
      approved: 0,
      published: 0,
      rejected: 0,
      scheduled: 0,
      all: 0,
    });
    setStats(null);
    setLoading(true);
    setLastContextId(contextId);
    isLoadingRef.current = false; // Reset loading ref to allow new fetch
  }

  const loadPosts = useCallback(
    async (
      forceRefresh: boolean = false,
      statuses?: string[],
      page: number = 1,
      limit: number = 10,
      search?: string,
    ) => {
      // Record the context ID when this request started
      const requestContextId = contextIdRef.current;

      // Protection against simultaneous duplicate requests
      if (isLoadingRef.current && !forceRefresh) {
        console.log(
          "⏳ [usePosts] loadPosts already running, ignoring duplicate call",
        );
        return;
      }

      // Verificar se há token de autenticação antes de fazer requisições
      const token =
        localStorage.getItem("access_token") || localStorage.getItem("token");
      if (!token) {
        setError("Usuário não autenticado. Faça login para acessar os posts.");
        setLoading(false);
        return;
      }

      isLoadingRef.current = true;
      setLoading(true);
      setError(null);
      try {
        // Se statuses for fornecido, carregar apenas os status especificados
        // Caso contrário, carregar todos (comportamento padrão para compatibilidade)
        const statusesToLoad = statuses || [
          "pending",
          "approved",
          "published",
          "rejected",
          "scheduled",
        ];

        const promises: Promise<any>[] = [];
        const statusOrder: string[] = [];

        if (statusesToLoad.includes("pending")) {
          promises.push(
            postsService.getPendingPosts(forceRefresh, page, limit, search),
          );
          statusOrder.push("pending");
        }
        if (statusesToLoad.includes("approved")) {
          promises.push(
            postsService.getApprovedPosts(forceRefresh, page, limit, search),
          );
          statusOrder.push("approved");
        }
        if (statusesToLoad.includes("published")) {
          promises.push(
            postsService.getPublishedPosts(forceRefresh, page, limit, search),
          );
          statusOrder.push("published");
        }
        if (statusesToLoad.includes("rejected")) {
          promises.push(postsService.getRejectedPosts(page, limit, search));
          statusOrder.push("rejected");
        }
        if (statusesToLoad.includes("scheduled")) {
          promises.push(postsService.getScheduledPosts(page, limit, search));
          statusOrder.push("scheduled");
        }
        if (statusesToLoad.includes("all")) {
          promises.push(
            postsService.getAllPosts(forceRefresh, page, limit, search),
          );
          statusOrder.push("all");
        }

        // Sempre carregar stats
        promises.push(postsService.getStats());

        const results = await Promise.all(promises);

        // CHECK: If context changed while fetching, ignore results
        if (contextIdRef.current !== requestContextId) {
          console.log(
            "🚫 [usePosts] Context changed during fetch, discarding results",
          );
          return;
        }

        // Extract results in correct order
        let resultIndex = 0;
        const pending = statusOrder.includes("pending")
          ? results[resultIndex++]
          : undefined;
        const approved = statusOrder.includes("approved")
          ? results[resultIndex++]
          : undefined;
        const published = statusOrder.includes("published")
          ? results[resultIndex++]
          : undefined;
        const rejected = statusOrder.includes("rejected")
          ? results[resultIndex++]
          : undefined;
        const scheduled = statusOrder.includes("scheduled")
          ? results[resultIndex++]
          : undefined;
        const all = statusOrder.includes("all")
          ? results[resultIndex++]
          : undefined;
        const statsData = results[resultIndex];

        // Update only states that were loaded
        setTotalCounts((prev) => {
          const next = { ...prev };
          if (pending !== undefined) next.pending = pending.totalCount;
          if (approved !== undefined) next.approved = approved.totalCount;
          if (published !== undefined) next.published = published.totalCount;
          if (rejected !== undefined) next.rejected = rejected.totalCount;
          if (scheduled !== undefined) next.scheduled = scheduled.totalCount;
          if (all !== undefined) next.all = all.totalCount;
          return next;
        });

        if (pending !== undefined) {
          setPendingPosts(pending.posts || []);
        }
        if (approved !== undefined) {
          setApprovedPosts(approved.posts || []);
        }
        if (published !== undefined) {
          setPublishedPosts(published.posts || []);
        }
        if (rejected !== undefined) {
          setRejectedPosts(rejected.posts || []);
        }
        if (scheduled !== undefined) {
          setScheduledPosts(scheduled.posts || []);
        }
        if (all !== undefined) {
          setAllPosts(all.posts || []);
        }
        setStats(statsData || null);
      } catch (err: any) {
        // Only set error if context is still the same
        if (contextIdRef.current === requestContextId) {
          setError(err.message || "Erro ao carregar posts");
        }
      } finally {
        // Only toggle loading if context is still the same
        if (contextIdRef.current === requestContextId) {
          setLoading(false);
          isLoadingRef.current = false;
        }
      }
    },
    [
      activeContext.type,
      activeContext.organization?.id,
      activeContext.clientCompany?.id,
    ],
  );

  // Ações principais
  const approvePost = useCallback(
    async (postId: number) => {
      setLoading(true);
      setError(null);
      try {
        await postsService.publishPost(postId);
        await loadPosts();
      } catch (err: any) {
        setError(err.message || "Erro ao aprovar post");
      } finally {
        setLoading(false);
      }
    },
    [loadPosts],
  );

  const rejectPost = useCallback(
    async (postId: number, reason: string) => {
      setLoading(true);
      setError(null);
      try {
        await postsService.rejectPost(postId, reason);
        await loadPosts();
      } catch (err: any) {
        setError(err.message || "Erro ao rejeitar post");
      } finally {
        setLoading(false);
      }
    },
    [loadPosts],
  );

  const revertToPending = useCallback(
    async (postId: number) => {
      setLoading(true);
      setError(null);
      try {
        await postsService.revertToPending(postId);
        await loadPosts();
      } catch (err: any) {
        setError(err.message || "Erro ao reverter post para pendente");
      } finally {
        setLoading(false);
      }
    },
    [loadPosts],
  );

  const updateSinglePost = useCallback(
    async (postId: number, updatedPostData?: any) => {
      try {
        let updatedPost = updatedPostData;

        // Se não recebeu os dados atualizados, buscar do servidor
        if (!updatedPost || !updatedPost.id) {
          try {
            updatedPost = await postsService.getPostById(postId);
          } catch (fetchError: any) {
            // Se o GET falhar (404, etc), tentar recarregar todos os posts
            console.warn(
              `⚠️ Erro ao buscar post ${postId} após edição:`,
              fetchError,
            );
            await loadPosts(true);
            return;
          }
        }

        if (!updatedPost || !updatedPost.id) {
          console.warn(
            `⚠️ Post ${postId} não encontrado após edição, recarregando todos os posts`,
          );
          await loadPosts(true);
          return;
        }

        // Normalizar hashtags se vierem como string
        if (updatedPost.hashtags && typeof updatedPost.hashtags === "string") {
          updatedPost.hashtags = updatedPost.hashtags
            .split(",")
            .map((h: string) => h.trim())
            .filter((h: string) => h);
        }

        // Atualizar o post na lista correspondente baseado no status
        const status = updatedPost.status?.toLowerCase();

        const updatePostInList = (
          setter: React.Dispatch<React.SetStateAction<Post[]>>,
        ) => {
          setter((prevPosts) => {
            const index = prevPosts.findIndex((p) => p.id === postId);
            if (index !== -1) {
              // Atualizar post existente mantendo a referência do array
              const newPosts = [...prevPosts];
              newPosts[index] = { ...prevPosts[index], ...updatedPost } as Post;
              return newPosts;
            } else {
              // Se não encontrou, pode ter mudado de status, recarregar tudo
              console.warn(
                `⚠️ Post ${postId} não encontrado na lista atual (status: ${status}), recarregando`,
              );
              return prevPosts;
            }
          });
        };

        // Atualizar na lista correta baseado no status
        if (status === "pending") {
          updatePostInList(setPendingPosts);
        } else if (status === "approved") {
          updatePostInList(setApprovedPosts);
        } else if (status === "published") {
          updatePostInList(setPublishedPosts);
        } else if (status === "rejected") {
          updatePostInList(setRejectedPosts);
        } else {
          // Status desconhecido ou mudou de status, recarregar tudo
          console.warn(
            `⚠️ Status desconhecido ou mudou: ${status}, recarregando todos os posts`,
          );
          await loadPosts(true);
        }
      } catch (err: any) {
        console.error(`❌ Erro ao atualizar post ${postId}:`, err);
        // Em caso de erro, recarregar todos os posts como fallback
        await loadPosts(true);
      }
    },
    [loadPosts],
  );

  const editPost = useCallback(
    async (postId: number, data: any) => {
      setLoading(true);
      setError(null);
      try {
        // O endpoint editPost retorna o post atualizado diretamente (PostResponse)
        const result = await postsService.editPost(postId, data);
        // O backend retorna PostResponse.from_domain(post).dict() via jsonify(response_data)
        // O apiService.put retorna response.json() diretamente, então result já é o objeto do post
        // Verificar se result tem id (é o post) ou se está dentro de result.data
        const updatedPost = (result?.id ? result : result?.data) || result;
        if (updatedPost && updatedPost.id) {
          await updateSinglePost(postId, updatedPost);
        } else {
          // Se não veio o post na resposta, buscar do servidor como fallback
          console.warn(
            `⚠️ Post não veio na resposta da edição, buscando do servidor...`,
          );
          await updateSinglePost(postId);
        }
      } catch (err: any) {
        setError(err.message || "Erro ao editar post");
        // Em caso de erro, recarregar todos os posts como fallback
        await loadPosts(true);
      } finally {
        setLoading(false);
      }
    },
    [updateSinglePost, loadPosts],
  );

  const schedulePost = useCallback(
    async (postId: number, date: string, time: string) => {
      setLoading(true);
      setError(null);
      try {
        await postsService.schedulePost(postId, { date, time });
        await loadPosts();
      } catch (err: any) {
        setError(err.message || "Erro ao agendar post");
      } finally {
        setLoading(false);
      }
    },
    [loadPosts],
  );

  const reschedulePostPublished = useCallback(
    async (postId: number, date: string, time: string) => {
      setLoading(true);
      setError(null);
      try {
        await postsService.reschedulePostPublished(postId, { date, time });
        await loadPosts();
      } catch (err: any) {
        setError(err.message || "Erro ao reagendar post");
      } finally {
        setLoading(false);
      }
    },
    [loadPosts],
  );

  const unschedulePost = useCallback(
    async (postId: number) => {
      setLoading(true);
      setError(null);
      try {
        await postsService.unschedulePost(postId);
        await loadPosts();
      } catch (err: any) {
        setError(err.message || "Erro ao remover agendamento");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [loadPosts],
  );

  const deletePost = useCallback(
    async (postId: number) => {
      // Verificar se já está sendo excluído
      if (deletingPosts.has(postId)) {
        console.log(
          `🔄 Post ${postId} já está sendo excluído, ignorando chamada duplicada`,
        );
        return;
      }

      setLoading(true);
      setError(null);

      // Adicionar à lista de posts sendo excluídos
      setDeletingPosts((prev) => new Set([...Array.from(prev), postId]));

      try {
        await postsService.deletePost(postId);
        await loadPosts();
        console.log(`✅ Post ${postId} excluído com sucesso`);
      } catch (err: any) {
        setError(err.message || "Erro ao excluir post");
        throw err; // Re-throw para que o componente saiba que houve erro
      } finally {
        setLoading(false);
        // Remover da lista de posts sendo excluídos
        setDeletingPosts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(postId);
          return newSet;
        });
      }
    },
    [loadPosts, deletingPosts],
  );

  // Upload e geração de imagem podem ser expandidos conforme necessário

  return {
    pendingPosts,
    approvedPosts,
    publishedPosts,
    rejectedPosts,
    scheduledPosts,
    allPosts,
    totalCounts,
    stats,
    loading,
    error,
    loadPosts,
    approvePost,
    rejectPost,
    revertToPending,
    editPost,
    schedulePost,
    reschedulePostPublished,
    unschedulePost,
    deletePost,
    uploadImage: postsService.uploadImage.bind(postsService),
    uploadVideo: postsService.uploadVideo.bind(postsService),
    // Adicione outras funções conforme necessário
  };
}
